import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, Loader2, Shield, ChevronLeft, ChevronRight, ToggleLeft, ToggleRight, Trash2, Plus, X, UserPlus } from 'lucide-react';
import axios from 'axios';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const sadApi = () => {
  const token = localStorage.getItem('sad_token');
  return axios.create({ baseURL: `${API}/api`, headers: { Authorization: `Bearer ${token}` } });
};

const ROLE_COLORS = {
  super_admin: 'bg-red-500/15 text-red-400',
  admin: 'bg-orange-500/15 text-orange-400',
  accountant: 'bg-cyan-500/15 text-cyan-400',
  support_agent: 'bg-yellow-500/15 text-yellow-400',
  content_manager: 'bg-violet-500/15 text-violet-400',
  personal_assistant: 'bg-pink-500/15 text-pink-400',
  shop_admin: 'bg-emerald-500/15 text-emerald-400',
  invoice_user: 'bg-purple-500/15 text-purple-400',
  user: 'bg-blue-500/15 text-blue-400',
};

const ROLE_LABELS = {
  super_admin: 'Super Admin', admin: 'Admin', accountant: 'Accountant',
  support_agent: 'Support Agent', content_manager: 'Content Manager',
  personal_assistant: 'Personal Assistant', shop_admin: 'Shop Admin',
  invoice_user: 'Invoice User', user: 'User',
};

const ADMIN_ROLES = ['super_admin', 'admin', 'accountant', 'support_agent', 'content_manager', 'personal_assistant'];

const SuperAdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'admin' });
  const [creating, setCreating] = useState(false);
  const [rolesInfo, setRolesInfo] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await sadApi().get('/sad/users', { params });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchUsers();
    sadApi().get('/sad/roles').then(r => setRolesInfo(r.data)).catch(() => {});
  }, [page, roleFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const toggleUser = async (userId, current) => {
    try {
      await sadApi().put(`/sad/users/${userId}/status?enabled=${!current}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteUser = async (userId, userName, role) => {
    if (role === 'super_admin') { toast.error('Cannot delete super admin'); return; }
    if (!window.confirm(`Delete user "${userName}" and their shop? This cannot be undone.`)) return;
    try {
      await sadApi().delete(`/sad/users/${userId}`);
      toast.success(`User "${userName}" deleted`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await sadApi().put(`/sad/admins/${userId}/role?role=${newRole}`);
      toast.success(`Role updated to ${ROLE_LABELS[newRole]}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change role'); }
  };

  const handleCreateAdmin = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('All fields are required'); return;
    }
    setCreating(true);
    try {
      const res = await sadApi().post('/sad/admins', createForm);
      toast.success(res.data.message);
      setShowCreateAdmin(false);
      setCreateForm({ name: '', email: '', password: '', role: 'admin' });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setCreating(false); }
  };

  const totalPages = Math.ceil(total / 20);
  const canManageRoles = rolesInfo?.can_manage_roles;
  const canDelete = rolesInfo?.can_delete;

  return (
    <SuperAdminLayout>
      <div className="space-y-5" data-testid="sad-users-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Users</h1>
            <p className="text-slate-400 text-sm">{total} total users</p>
          </div>
          {canManageRoles && (
            <button onClick={() => setShowCreateAdmin(true)}
              className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600 flex items-center gap-2"
              data-testid="create-admin-btn">
              <UserPlus className="w-4 h-4" /> Create Admin
            </button>
          )}
        </div>

        {/* Search & Filter */}
        <div className="flex gap-3 flex-wrap">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none" />
            </div>
          </form>
          <select value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white focus:border-amber-500 focus:outline-none"
            data-testid="role-filter">
            <option value="">All Roles</option>
            {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        ) : (
          <div className="bg-slate-800/50 border border-slate-700 rounded overflow-hidden">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-slate-700 text-left">
                  <th className="px-4 py-3 text-slate-400 font-medium">Name</th>
                  <th className="px-4 py-3 text-slate-400 font-medium">Email</th>
                  <th className="px-4 py-3 text-slate-400 font-medium">Role</th>
                  <th className="px-4 py-3 text-slate-400 font-medium">Joined</th>
                  <th className="px-4 py-3 text-slate-400 font-medium text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <motion.tr key={u.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="border-b border-slate-700/50 hover:bg-slate-800/80">
                    <td className="px-4 py-3 text-white font-medium">{u.name || '-'}</td>
                    <td className="px-4 py-3 text-slate-300">{u.email}</td>
                    <td className="px-4 py-3">
                      {canManageRoles && ADMIN_ROLES.includes(u.role) && u.role !== 'super_admin' ? (
                        <select value={u.role} onChange={e => changeRole(u.id, e.target.value)}
                          className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-white focus:border-amber-500 focus:outline-none"
                          data-testid={`role-select-${u.id}`}>
                          {ADMIN_ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                      ) : (
                        <span className={`inline-block px-2.5 py-1 rounded text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-slate-700 text-slate-400'}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.role === 'super_admin' ? (
                        <span className="text-xs text-slate-500">Protected</span>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => toggleUser(u.id, u.is_verified)}
                            className="inline-flex items-center" data-testid={`toggle-user-${u.id}`}>
                            {u.is_verified ? <ToggleRight className="w-6 h-6 text-emerald-400" /> : <ToggleLeft className="w-6 h-6 text-slate-500" />}
                          </button>
                          {canDelete && (
                            <button onClick={() => deleteUser(u.id, u.name || u.email, u.role)}
                              className="p-1 hover:bg-slate-700 rounded text-slate-500 hover:text-red-400"
                              data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 bg-slate-800 border border-slate-700 rounded text-white disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-slate-400 text-sm">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-2 bg-slate-800 border border-slate-700 rounded text-white disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      <AnimatePresence>
        {showCreateAdmin && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCreateAdmin(false)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md bg-[#1e293b] border border-slate-700 rounded-lg p-6"
                onClick={e => e.stopPropagation()} data-testid="create-admin-modal">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Create Admin</h3>
                  <button onClick={() => setShowCreateAdmin(false)} className="p-1 hover:bg-slate-700 rounded"><X className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Full Name</label>
                    <input type="text" value={createForm.name} onChange={e => setCreateForm(v => ({ ...v, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none"
                      placeholder="John Doe" data-testid="admin-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Email</label>
                    <input type="email" value={createForm.email} onChange={e => setCreateForm(v => ({ ...v, email: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none"
                      placeholder="admin@vendfactura.com" data-testid="admin-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Password</label>
                    <input type="password" value={createForm.password} onChange={e => setCreateForm(v => ({ ...v, password: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none"
                      placeholder="Min 8 characters" data-testid="admin-password" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-1">Role</label>
                    <select value={createForm.role} onChange={e => setCreateForm(v => ({ ...v, role: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:border-amber-500 focus:outline-none"
                      data-testid="admin-role">
                      {ADMIN_ROLES.filter(r => r !== 'super_admin').map(r => (
                        <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                      ))}
                    </select>
                  </div>
                  {/* Role description */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded p-3">
                    <p className="text-xs text-slate-400">
                      {createForm.role === 'admin' && 'Full access except role management.'}
                      {createForm.role === 'accountant' && 'Read-only: Revenue, Subscriptions, Shops.'}
                      {createForm.role === 'support_agent' && 'Users, Shops, Tickets. Can toggle status.'}
                      {createForm.role === 'content_manager' && 'Catalog only. Edit products, approve submissions.'}
                      {createForm.role === 'personal_assistant' && 'Full access except role management.'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button onClick={() => setShowCreateAdmin(false)} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded text-sm">Cancel</button>
                  <button onClick={handleCreateAdmin} disabled={creating}
                    className="flex-1 px-4 py-2 bg-amber-500 text-white rounded text-sm hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-create-admin">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Create
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </SuperAdminLayout>
  );
};

export default SuperAdminUsers;
